import React, { FC, Fragment, useEffect, useMemo, useState } from "react";
import { Avatar, Box, Button, Flex, Loader, Pagination, SegmentedControl, Text, Tooltip } from "@mantine/core";
import { useElementSize } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { IconChevronRight, IconChevronsLeft, IconChevronsRight, IconTable, IconTimeline } from "@tabler/icons-react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import Fetch from "@/utils/Fetch";
import * as Enums from "@/utils/enums";
import OptionService from "@/services/option/option-service";
import JobStatusService from "@/services/job/job-status-service";
import employeeService from "@/services/employee/employee-service";
import ScStatusData from "@/PageComponents/Table/Table/ScStatusData";
import { scColourMapping } from "@/PageComponents/Table/table-helper";
import JobSectionCard from "@/PageComponents/Job/JobSectionCard";
import styles from "@/PageComponents/Job/JobHistory.module.css";

dayjs.extend(utc);

type AuditEntry = {
    ID: string;
    AuditLogID: string;
    PropertyName: string;
    OriginalValue: string | null;
    NewValue: string | null;
    UserName: string;
    EventDateUTC: string;
};

type HistoryView = 'table' | 'timeline';

const TABLE_PAGE_SIZE = 10;
const TIMELINE_PAGE_SIZE = 20;
const VIEW_STORAGE_KEY = 'jobHistoryView';
const STATUS_PROPERTY = 'JobCardStatusID';
// a change this long gets its original and new value on their own lines in the timeline
const LONG_CHANGE_LENGTH = 60;

// Friendlier names for the job's audited properties; anything else is split from its property name
const PROPERTY_LABELS: Record<string, string> = {
    JobCardStatusID: 'Status',
    CustomerID: 'Customer',
    CustomerContactID: 'Contact',
    LocationID: 'Location',
    JobTypeID: 'Job type',
    VanID: 'Van',
    StoreID: 'Store',
    StartDate: 'Start date',
    EndDate: 'End date',
    ClosedDate: 'Closed date',
    IsClosed: 'Closed',
    SignatureName: 'Signature name',
    SignatureTechnicianName: 'Technician signature name',
    EmployeeID: 'Employee',
};

const propertyLabel = (name: string) => PROPERTY_LABELS[name] ?? (() => {
    const words = name.replace(/ID$/, '').replace(/^Is(?=[A-Z])/, '').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/([a-zA-Z])(\d)/g, '$1 $2').toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
})();

const eventDate = (entry: AuditEntry) => dayjs.utc(entry.EventDateUTC).local();

// Booleans and dates come through as text
const displayValue = (value: string | null) => {
    if (value === null || value === undefined || value === '') return '';
    if (value === 'True') return 'Yes';
    if (value === 'False') return 'No';
    if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(value)) return dayjs(value).format('D MMM YYYY, HH:mm');
    return value;
};

const isStatus = (entry: AuditEntry) => entry.PropertyName === STATUS_PROPERTY;

const readStoredView = (): HistoryView => {
    try {
        return localStorage.getItem(VIEW_STORAGE_KEY) === 'timeline' ? 'timeline' : 'table';
    } catch {
        return 'table';
    }
};

const fetchHistory = async (jobId: string, pageSize: number, pageIndex: number, searchPhrase = '') => {
    const result = await Fetch.post({
        url: '/AuditLog/GetAuditLogDetail',
        params: { pageSize, pageIndex, searchPhrase, recordID: jobId },
    });
    if (!Array.isArray(result?.Results)) throw new Error(result?.serverMessage || 'Could not load the job history');
    return { results: result.Results as AuditEntry[], total: (result.TotalResults ?? 0) as number };
};

/* Lookups shared by both views: status colours by name, people by user name, and the status before each status change */
const useHistoryLookups = (jobId: string, enabled: boolean, retrigger: any) => {
    const { data: statusColors } = useQuery(['jobHistory', 'statusColors'], async () => {
        const result = await JobStatusService.getJobStatuses(false);
        const statuses: any[] = Array.isArray(result) ? result : result?.Results ?? [];
        return Object.fromEntries(statuses.map(x => [x.Description, x.DisplayColor])) as Record<string, string>;
    }, { enabled, staleTime: 5 * 60_000 });

    const { data: people } = useQuery(['jobHistory', 'people'], async () => {
        const result = await employeeService.getEmployees(null, true);
        const employees: any[] = Array.isArray(result) ? result : result?.Results ?? [];
        return Object.fromEntries(employees.filter(x => x.UserName).map(x => [x.UserName.toLowerCase(), { name: x.FullName, color: x.DisplayColor }])) as Record<string, { name: string; color?: string }>;
    }, { enabled, staleTime: 5 * 60_000 });

    // Status changes are logged without their original value, so it comes from the status change before it.
    // The search phrase matches the property name, which gives just the status changes.
    const { data: previousStatuses } = useQuery(['jobHistory', jobId, 'statuses', retrigger], async () => {
        const { results } = await fetchHistory(jobId, 500, 0, STATUS_PROPERTY);
        const changes = results.filter(isStatus).sort((a, b) => a.EventDateUTC.localeCompare(b.EventDateUTC));
        return Object.fromEntries(changes.map((x, i) => [x.ID, i === 0 ? null : changes[i - 1].NewValue])) as Record<string, string | null>;
    }, { enabled });

    // the account's own names for the custom fields; same query as the job page, so it is normally already cached
    const { data: customFields } = useQuery(['customFieldsData', Enums.Module.JobCard],
        () => Fetch.get({ url: `/Option?module=${Enums.Module.JobCard}` }), { enabled, staleTime: 5 * 60_000 });
    const customLabels = useMemo(() => {
        const labels = OptionService.getJobCustomFields(customFields?.Results);
        return {
            CustomField1: labels.fieldLabel1, CustomField2: labels.fieldLabel2, CustomField3: labels.fieldLabel3, CustomField4: labels.fieldLabel4,
            CustomDate1: labels.dateFieldLabel1, CustomDate2: labels.dateFieldLabel2,
            CustomFilter1: labels.filterFieldLabel1, CustomFilter2: labels.filterFieldLabel2,
            CustomNumber1: labels.numberFieldLabel1, CustomNumber2: labels.numberFieldLabel2,
        } as Record<string, string>;
    }, [customFields]);
    const label = (propertyName: string) => customLabels[propertyName]?.trim() || propertyLabel(propertyName);

    const person = (userName: string) => people?.[userName?.toLowerCase()] ?? { name: userName, color: undefined };
    const statusColor = (status: string | null) => (status && statusColors?.[status]) || undefined;
    // undefined until known; null for the status the job was created with
    const previousStatus = (entry: AuditEntry) => entry.OriginalValue ?? previousStatuses?.[entry.ID];

    return { person, statusColor, previousStatus, label };
};

type Lookups = ReturnType<typeof useHistoryLookups>;

// employee colours are hex values or the names used across ServCraft (Red, LightGrey, ...)
const PersonAvatar: FC<{ name: string; color?: string; size: number }> = ({ name, color, size }) =>
    <Avatar name={name} size={size} radius={'xl'} variant={'filled'} autoContrast
            color={(color?.startsWith('#') ? color : color && scColourMapping[color as keyof typeof scColourMapping]) || 'scBlue'}
            styles={{ placeholder: { fontSize: Math.round(size * 0.4), fontWeight: 600 } }} />;

const StatusBadge: FC<{ status: string | null; lookups: Lookups }> = ({ status, lookups }) =>
    status ? <Box component={'span'} className={styles.status}><ScStatusData value={status} color={lookups.statusColor(status)} shrink /></Box> : <Nil />;

const Nil = () => <span className={styles.nil}>—</span>;

/* ---------- Table view: Property, Original value, New value, Date, User ---------- */

// reports whether the value is cut off; an open row shows it in full, so it is only measured while closed
const ValueCell: FC<{ value: string; className: string; rowOpen: boolean; onOverflow: (overflows: boolean) => void }> = ({ value, className, rowOpen, onOverflow }) => {
    const { ref, width } = useElementSize();
    useEffect(() => {
        const el = ref.current as HTMLElement | null;
        if (!el || rowOpen) return;
        // measure the text itself: scrollWidth rounds away overflows of under a pixel
        const range = document.createRange();
        range.selectNodeContents(el);
        onOverflow(range.getBoundingClientRect().width > el.getBoundingClientRect().width + 0.05);
    }, [width, value, rowOpen]);
    return <span ref={ref} className={`${styles.value} ${className}`}>{value}</span>;
};

const HistoryTableRow: FC<{ entry: AuditEntry; lookups: Lookups }> = ({ entry, lookups }) => {
    const [overflow, setOverflow] = useState({ from: false, to: false });
    const [open, setOpen] = useState(false);
    const canOpen = overflow.from || overflow.to;
    const status = isStatus(entry);
    const from = displayValue(entry.OriginalValue);
    const to = displayValue(entry.NewValue);
    const who = lookups.person(entry.UserName);

    return (
        <tr className={`${canOpen ? styles.canOpen : ''} ${open ? styles.open : ''}`}
            onClick={canOpen ? () => setOpen(o => !o) : undefined}
            aria-expanded={canOpen ? open : undefined}>
            <td className={styles.property} title={entry.PropertyName}>
                <IconChevronRight size={14} className={styles.expandIcon} aria-hidden />
                {lookups.label(entry.PropertyName)}
            </td>
            <td>
                {status ? <StatusBadge status={lookups.previousStatus(entry) ?? null} lookups={lookups} /> :
                    from ? <ValueCell value={from} className={styles.was} rowOpen={open} onOverflow={o => setOverflow(x => x.from === o ? x : { ...x, from: o })} /> : <Nil />}
            </td>
            <td>
                {status ? <StatusBadge status={entry.NewValue} lookups={lookups} /> :
                    to ? <ValueCell value={to} className={styles.now} rowOpen={open} onOverflow={o => setOverflow(x => x.to === o ? x : { ...x, to: o })} /> : <Nil />}
            </td>
            <td className={styles.date}>{eventDate(entry).format('D MMM YYYY, HH:mm')}</td>
            <td>
                <Tooltip label={entry.UserName} disabled={who.name === entry.UserName} openDelay={300}>
                    <span className={styles.who}>
                        <PersonAvatar name={who.name} color={who.color} size={20} />
                        <span className={styles.whoName}>{who.name}</span>
                    </span>
                </Tooltip>
            </td>
        </tr>
    );
};

const HistoryPager: FC<{ page: number; total: number; onChange: (page: number) => void }> = ({ page, total, onChange }) =>
    <Pagination.Root total={total} value={page} onChange={onChange} size={'sm'} siblings={1} classNames={{ control: styles.pagerControl }}>
        <Flex component={'nav'} aria-label={'History pages'} justify={'center'} align={'center'} gap={6} mt={10}>
            <Pagination.First icon={IconChevronsLeft} aria-label={'First page'} />
            <Pagination.Previous icon={() => <span>Back</span>} aria-label={'Previous page'} className={styles.pagerLabel} />
            <Pagination.Items />
            <Pagination.Next icon={() => <span>Next</span>} aria-label={'Next page'} className={styles.pagerLabel} />
            <Pagination.Last icon={IconChevronsRight} aria-label={'Last page'} />
        </Flex>
    </Pagination.Root>;

const HistoryTable: FC<{ jobId: string; retrigger: any; lookups: Lookups }> = ({ jobId, retrigger, lookups }) => {
    const [page, setPage] = useState(1);
    useEffect(() => setPage(1), [retrigger]);

    const { data, isLoading, isError } = useQuery(['jobHistory', jobId, 'table', page, retrigger],
        () => fetchHistory(jobId, TABLE_PAGE_SIZE, page - 1), { keepPreviousData: true });

    if (isLoading) return <Flex justify={'center'} py={'md'}><Loader size={'sm'} /></Flex>;
    if (isError || !data) return <Text c={'red.7'} size={'sm'}>Could not load the job history.</Text>;

    const pages = Math.max(1, Math.ceil(data.total / TABLE_PAGE_SIZE));
    return <>
        <div className={styles.tableWrap}>
            <table className={styles.table}>
                <thead>
                <tr>
                    <th>Property</th>
                    <th>Original value</th>
                    <th>New value</th>
                    <th>Date</th>
                    <th>User</th>
                </tr>
                </thead>
                <tbody>
                {data.results.map(entry => <HistoryTableRow key={entry.ID} entry={entry} lookups={lookups} />)}
                </tbody>
            </table>
        </div>
        {pages > 1 && <HistoryPager page={page} total={pages} onChange={setPage} />}
    </>;
};

/* ---------- Timeline view: grouped by day, newest first ---------- */

const dayLabel = (date: dayjs.Dayjs) => {
    const today = dayjs();
    if (date.isSame(today, 'day')) return 'Today';
    if (date.isSame(today.subtract(1, 'day'), 'day')) return 'Yesterday';
    return date.format(date.isSame(today, 'year') ? 'dddd D MMMM' : 'dddd D MMMM YYYY');
};

const TimelineChange: FC<{ entry: AuditEntry; lookups: Lookups }> = ({ entry, lookups }) => {
    const label = lookups.label(entry.PropertyName);
    if (isStatus(entry)) {
        return lookups.previousStatus(entry) !== null ?
            <div className={styles.change}>Status changed to <StatusBadge status={entry.NewValue} lookups={lookups} /></div> :
            <div className={styles.change}>Job created with status <StatusBadge status={entry.NewValue} lookups={lookups} /></div>;
    }
    const from = displayValue(entry.OriginalValue);
    const to = displayValue(entry.NewValue);
    if (!from && !to) return <div className={styles.change}>{label} changed</div>;
    if (!from) return <div className={styles.change}><span>{label} set to <b>{to}</b></span></div>;
    if (!to) return <div className={styles.change}><span>{label} cleared, was <span className={styles.was}>{from}</span></span></div>;
    if ((from + to).length <= LONG_CHANGE_LENGTH) {
        return <div className={styles.change}><span>{label} changed from <span className={styles.was}>{from}</span> to <b>{to}</b></span></div>;
    }
    return <>
        <div className={styles.change}>{label} changed</div>
        <dl className={styles.longChange}>
            <dt>Original</dt>
            <dd className={styles.was}>{from}</dd>
            <dt>New</dt>
            <dd>{to}</dd>
        </dl>
    </>;
};

const HistoryTimeline: FC<{ jobId: string; retrigger: any; lookups: Lookups }> = ({ jobId, retrigger, lookups }) => {
    const [pageSize, setPageSize] = useState(TIMELINE_PAGE_SIZE);
    useEffect(() => setPageSize(TIMELINE_PAGE_SIZE), [retrigger]);

    const { data, isLoading, isError, isFetching } = useQuery(['jobHistory', jobId, 'timeline', pageSize, retrigger],
        () => fetchHistory(jobId, pageSize, 0), { keepPreviousData: true });

    const days = useMemo(() => {
        const groups: { key: string; date: dayjs.Dayjs; entries: AuditEntry[] }[] = [];
        (data?.results ?? []).forEach(entry => {
            const date = eventDate(entry);
            const key = date.format('YYYY-MM-DD');
            let group = groups.find(x => x.key === key);
            if (!group) groups.push(group = { key, date, entries: [] });
            group.entries.push(entry);
        });
        return groups;
    }, [data]);

    if (isLoading) return <Flex justify={'center'} py={'md'}><Loader size={'sm'} /></Flex>;
    if (isError || !data) return <Text c={'red.7'} size={'sm'}>Could not load the job history.</Text>;

    const remaining = data.total - data.results.length;
    return <div className={styles.timeline}>
        {days.map(day => <Fragment key={day.key}>
            <div className={styles.day}><span>{dayLabel(day.date)}</span></div>
            {day.entries.map((entry, i) => {
                const previous = day.entries[i - 1];
                const time = eventDate(entry).format('HH:mm');
                // a follow-on change by the same person within the hour drops the avatar and name
                const continued = previous && previous.UserName === entry.UserName &&
                    eventDate(previous).diff(eventDate(entry), 'minute') < 60;
                const who = lookups.person(entry.UserName);
                return continued ?
                    <div key={entry.ID} className={`${styles.row} ${styles.continued}`}>
                        <time className={styles.gutterTime}>{time}</time>
                        <div><TimelineChange entry={entry} lookups={lookups} /></div>
                    </div> :
                    <div key={entry.ID} className={styles.row}>
                        <PersonAvatar name={who.name} color={who.color} size={32} />
                        <div>
                            <div className={styles.rowHead}>
                                <b title={who.name !== entry.UserName ? entry.UserName : undefined}>{who.name}</b>
                                <time>{time}</time>
                            </div>
                            <TimelineChange entry={entry} lookups={lookups} />
                        </div>
                    </div>;
            })}
        </Fragment>)}
        {remaining > 0 &&
            <Button variant={'default'} fullWidth mt={8} loading={isFetching}
                    onClick={() => setPageSize(size => size + TIMELINE_PAGE_SIZE)}>
                Show {Math.min(remaining, TIMELINE_PAGE_SIZE)} older {Math.min(remaining, TIMELINE_PAGE_SIZE) === 1 ? 'change' : 'changes'}
            </Button>}
    </div>;
};

/* ---------- The History section ---------- */

/* Everything recorded against the job (field and status changes), as a paged table or a timeline.
   Hidden until something has happened beyond creating the job; starts closed. */
const JobHistory: FC<{ jobId: string; retrigger?: any }> = ({ jobId, retrigger }) => {
    const [view, setView] = useState<HistoryView>(readStoredView);
    const [open, setOpen] = useState(false);
    const lookups = useHistoryLookups(jobId, open, retrigger);

    // the first entry tells us whether there is anything beyond the job being created
    const { data: summary } = useQuery(['jobHistory', jobId, 'summary', retrigger], () => fetchHistory(jobId, 1, 0));
    const total = summary?.total ?? 0;
    const onlyCreated = total === 1 && isStatus(summary!.results[0]);

    const changeView = (value: string) => {
        const next = value as HistoryView;
        setView(next);
        try {
            localStorage.setItem(VIEW_STORAGE_KEY, next);
        } catch {
            // the choice just isn't remembered
        }
    };

    const viewToggle = <SegmentedControl size={'xs'} aria-label={'History view'} value={view} onChange={changeView}
                                          classNames={{ root: styles.viewToggle, indicator: styles.viewIndicator, label: styles.viewLabel }}
                                          data={[
                                              { value: 'table', label: <Flex align={'center'} gap={6}><IconTable size={15} />Table</Flex> },
                                              { value: 'timeline', label: <Flex align={'center'} gap={6}><IconTimeline size={15} />Timeline</Flex> },
                                          ]} />;

    return (
        <JobSectionCard title={'History'} count={total || undefined} collapsible onOpenChange={setOpen}
                        hidden={!summary || total === 0 || onlyCreated} titleAside={viewToggle}>
            {view === 'table' ?
                <HistoryTable jobId={jobId} retrigger={retrigger} lookups={lookups} /> :
                <HistoryTimeline jobId={jobId} retrigger={retrigger} lookups={lookups} />}
        </JobSectionCard>
    );
};

export default JobHistory;
